import { Actor } from "./actor.js";
import { Cell } from "./cell.js";
import { Inventory, msToProtoTime } from "./index.js";
import { wasmFuncs } from "./module.js";
import { CVector, getStringAt, getWasmReader, makeAccessorClassObj, Struct, } from "./struct.js";
export class PlayerSeat extends Struct {
    get actor() {
        const ptr = wasmFuncs.PlayerSeat_get_actor(this._ptr);
        if (ptr === 0)
            return null;
        return new Actor(ptr);
    }
    get inputs() {
        return wasmFuncs.PlayerSeat_get_inputs(this._ptr);
    }
    set inputs(val) {
        wasmFuncs.PlayerSeat_set_inputs(this._ptr, val);
    }
    get releasedInputs() {
        return wasmFuncs.PlayerSeat_get_released_inputs(this._ptr);
    }
    set releasedInputs(val) {
        wasmFuncs.PlayerSeat_set_released_inputs(this._ptr, val);
    }
    get displayedHint() {
        return getStringAt(wasmFuncs.PlayerSeat_get_displayed_hint(this._ptr));
    }
    hasPerspective() {
        return !!wasmFuncs.PlayerSeat_has_perspective(this._ptr);
    }
    getPossibleActions(level) {
        return wasmFuncs.PlayerSeat_get_possible_actions(this._ptr, level._ptr);
    }
}
export class LevelMetadata extends Struct {
    get title() {
        return getStringAt(wasmFuncs.LevelMetadata_get_title(this._ptr));
    }
    get author() {
        return getStringAt(wasmFuncs.LevelMetadata_get_author(this._ptr));
    }
    get defaultHint() {
        return getStringAt(wasmFuncs.LevelMetadata_get_default_hint(this._ptr));
    }
    get cameraWidth() {
        return wasmFuncs.LevelMetadata_get_camera_width(this._ptr);
    }
    get cameraHeight() {
        return wasmFuncs.LevelMetadata_get_camera_height(this._ptr);
    }
    get cc1Boots() {
        return !!wasmFuncs.LevelMetadata_get_cc1_boots(this._ptr);
    }
    set cc1Boots(val) {
        wasmFuncs.LevelMetadata_set_cc1_boots(this._ptr, val);
    }
    get wiresHidden() {
        return !!wasmFuncs.LevelMetadata_get_wires_hidden(this._ptr);
    }
    set wiresHidden(val) {
        wasmFuncs.LevelMetadata_set_wires_hidden(this._ptr, val);
    }
    get c2gCommand() {
        return getStringAt(wasmFuncs.LevelMetadata_get_c2g_command(this._ptr));
    }
    get rngBlob4Pat() {
        return !!wasmFuncs.LevelMetadata_get_rng_blob_4pat(this._ptr);
    }
    get rngBlobDeterministic() {
        return !!wasmFuncs.LevelMetadata_get_rng_blob_deterministic(this._ptr);
    }
}
export class VectorUint8 extends CVector {
    getItemSize() {
        return 1;
    }
    instantiateItem(ptr) {
        return getWasmReader().getUint8(ptr);
    }
}
export class Replay extends Struct {
    get randomForceFloorDirection() {
        return wasmFuncs.Replay_get_rff_direction(this._ptr);
    }
    set randomForceFloorDirection(val) {
        wasmFuncs.Replay_set_rff_direction(this._ptr, val);
    }
    get rngBlob() {
        return wasmFuncs.Replay_get_rng_blob(this._ptr);
    }
    set rngBlob(val) {
        wasmFuncs.Replay_set_rng_blob(this._ptr, val);
    }
    get inputs() {
        return new VectorUint8(wasmFuncs.Replay_get_inputs_ptr(this._ptr));
    }
}
export var HashSettings;
(function (HashSettings) {
    HashSettings[HashSettings["IGNORE_BLOCK_ORDER"] = 1] = "IGNORE_BLOCK_ORDER";
    HashSettings[HashSettings["IGNORE_PLAYER_DIRECTION"] = 2] = "IGNORE_PLAYER_DIRECTION";
    // TODO: IGNORE_PLAYER_BUMP = 1 << 2,
    HashSettings[HashSettings["IGNORE_MIMIC_PARITY"] = 8] = "IGNORE_MIMIC_PARITY";
    HashSettings[HashSettings["IGNORE_TEETH_PARITY"] = 16] = "IGNORE_TEETH_PARITY";
})(HashSettings || (HashSettings = {}));
export class VectorPlayerSeat extends CVector {
    getItemSize() {
        return wasmFuncs._libnotcc_bind_PlayerSeat_size();
    }
    instantiateItem(ptr) {
        return new PlayerSeat(ptr);
    }
}
export class Glitch extends Struct {
    get glitchKind() {
        return wasmFuncs.Glitch_get_glitch_kind(this._ptr);
    }
    get location() {
        const loc = wasmFuncs.Glitch_get_location_xy(this._ptr);
        return [loc & 0xff, loc >> 8];
    }
    get specifier() {
        return wasmFuncs.Glitch_get_specifier(this._ptr);
    }
    get happensAt() {
        return wasmFuncs.Glitch_get_happens_at(this._ptr);
    }
    isCrashing() {
        return !!wasmFuncs.Glitch_is_crashing(this._ptr);
    }
    toGlitchInfo() {
        const pos = this.location;
        return {
            glitchKind: this.glitchKind,
            location: { x: pos[0], y: pos[1] },
            happensAt: msToProtoTime(Number((this.happensAt * 1000n) / 60n)),
            specifier: this.specifier,
        };
    }
}
export class VectorGlitch extends CVector {
    getItemSize() {
        return wasmFuncs._libnotcc_bind_Glitch_size();
    }
    instantiateItem(ptr) {
        return new Glitch(ptr);
    }
}
export class ActorList {
    get length() {
        return this.level.actor_n;
    }
    *[Symbol.iterator]() {
        const length = this.length;
        for (let idx = 0; idx < length; idx += 1) {
            yield this[idx];
        }
    }
    getItem(idx) {
        return new Actor(getWasmReader().getUint32(this.level.actors_ptr + idx * 4));
    }
    constructor(level) {
        this.level = level;
        return makeAccessorClassObj(this);
    }
    getItemSize() {
        return wasmFuncs._libnotcc_bind_Actor_size();
    }
    instantiateItem(ptr) {
        return new Actor(ptr);
    }
}
export var SfxBit;
(function (SfxBit) {
    SfxBit[SfxBit["FIRST"] = 1] = "FIRST";
    SfxBit[SfxBit["RECESSED_WALL"] = 1] = "RECESSED_WALL";
    SfxBit[SfxBit["EXPLOSION"] = 2] = "EXPLOSION";
    SfxBit[SfxBit["SPLASH"] = 4] = "SPLASH";
    SfxBit[SfxBit["TELEPORT"] = 8] = "TELEPORT";
    SfxBit[SfxBit["THIEF"] = 16] = "THIEF";
    SfxBit[SfxBit["DIRT_CLEAR"] = 32] = "DIRT_CLEAR";
    SfxBit[SfxBit["BUTTON_PRESS"] = 64] = "BUTTON_PRESS";
    SfxBit[SfxBit["BLOCK_PUSH"] = 128] = "BLOCK_PUSH";
    SfxBit[SfxBit["FORCE_FLOOR_SLIDE"] = 256] = "FORCE_FLOOR_SLIDE";
    SfxBit[SfxBit["PLAYER_BONK"] = 512] = "PLAYER_BONK";
    SfxBit[SfxBit["WATER_STEP"] = 1024] = "WATER_STEP";
    SfxBit[SfxBit["SLIDE_STEP"] = 2048] = "SLIDE_STEP";
    SfxBit[SfxBit["ICE_SLIDE"] = 4096] = "ICE_SLIDE";
    SfxBit[SfxBit["FIRE_STEP"] = 8192] = "FIRE_STEP";
    SfxBit[SfxBit["ITEM_PICKUP"] = 16384] = "ITEM_PICKUP";
    SfxBit[SfxBit["SOCKET_UNLOCK"] = 32768] = "SOCKET_UNLOCK";
    SfxBit[SfxBit["DOOR_UNLOCK"] = 65536] = "DOOR_UNLOCK";
    SfxBit[SfxBit["CHIP_WIN"] = 131072] = "CHIP_WIN";
    SfxBit[SfxBit["MELINDA_WIN"] = 262144] = "MELINDA_WIN";
    SfxBit[SfxBit["CHIP_DEATH"] = 524288] = "CHIP_DEATH";
    SfxBit[SfxBit["MELINDA_DEATH"] = 1048576] = "MELINDA_DEATH";
    SfxBit[SfxBit["LAST"] = 1048576] = "LAST";
})(SfxBit || (SfxBit = {}));
export const SFX_BITS_CONTINUOUS = SfxBit.FORCE_FLOOR_SLIDE | SfxBit.ICE_SLIDE;
export class LastPlayerInfo extends Struct {
    get inventory() {
        return new Inventory(wasmFuncs.LastPlayerInfo_get_inventory_ptr(this._ptr));
    }
    get exitN() {
        return wasmFuncs.LastPlayerInfo_get_exit_n(this._ptr);
    }
    get isMale() {
        return wasmFuncs.LastPlayerInfo_get_is_male(this._ptr);
    }
}
export const DETERMINISTIC_BLOB_MOD = 0x55;
export class Level extends Struct {
    static unalloc(ptr) {
        wasmFuncs.Level_uninit(ptr);
    }
    // Basic
    get width() {
        return wasmFuncs.Level_get_width(this._ptr);
    }
    get height() {
        return wasmFuncs.Level_get_height(this._ptr);
    }
    get currentTick() {
        return wasmFuncs.Level_get_current_tick(this._ptr);
    }
    get currentSubtick() {
        return wasmFuncs.Level_get_current_subtick(this._ptr);
    }
    get gameState() {
        return wasmFuncs.Level_get_game_state(this._ptr);
    }
    get metadata() {
        return new LevelMetadata(wasmFuncs.Level_get_metadata_ptr(this._ptr));
    }
    get builtinReplay() {
        const ptr = wasmFuncs.Level_get_builtin_replay(this._ptr);
        if (ptr === 0)
            return null;
        return new Replay(ptr);
    }
    get actors_ptr() {
        return wasmFuncs.Level_get_actors(this._ptr);
    }
    get actor_n() {
        return wasmFuncs.Level_get_actors_n(this._ptr);
    }
    get actors() {
        return new ActorList(this);
    }
    get lastWonPlayerInfo() {
        return new LastPlayerInfo(wasmFuncs.Level_get_last_won_player_info_ptr(this._ptr));
    }
    get ignoreBonusFlags() {
        return !!wasmFuncs.Level_get_ignore_bonus_flags(this._ptr);
    }
    set ignoreBonusFlags(val) {
        wasmFuncs.Level_set_ignore_bonus_flags(this._ptr, val);
    }
    setProviderInputs(ip) {
        const seats = this.playerSeats;
        for (let idx = 0; idx < seats.length; idx += 1) {
            seats[idx].inputs = ip.getInput(this.subticksPassed(), idx);
        }
    }
    tick() {
        wasmFuncs.Level_tick(this._ptr);
    }
    getCell(x, y) {
        const cell = new Cell(wasmFuncs.Level_get_cell_xy(this._ptr, x, y));
        return cell;
    }
    get glitches() {
        return new VectorGlitch(wasmFuncs.Level_get_glitches_ptr(this._ptr));
    }
    // Player
    get playerSeats() {
        return new VectorPlayerSeat(wasmFuncs.Level_get_player_seats_ptr(this._ptr));
    }
    get playersLeft() {
        return wasmFuncs.Level_get_players_left(this._ptr);
    }
    // Metrics
    get timeLeft() {
        return wasmFuncs.Level_get_time_left(this._ptr);
    }
    set timeLeft(val) {
        wasmFuncs.Level_set_time_left(this._ptr, val);
    }
    get timeStopped() {
        return !!wasmFuncs.Level_get_time_stopped(this._ptr);
    }
    get chipsLeft() {
        return wasmFuncs.Level_get_chips_left(this._ptr);
    }
    get bonusPoints() {
        return wasmFuncs.Level_get_bonus_points(this._ptr);
    }
    // Rng
    get rng1() {
        return wasmFuncs.Level_get_rng1(this._ptr);
    }
    get rng2() {
        return wasmFuncs.Level_get_rng2(this._ptr);
    }
    get rngBlob() {
        return wasmFuncs.Level_get_rng_blob(this._ptr);
    }
    set rngBlob(val) {
        wasmFuncs.Level_set_rng_blob(this._ptr, val);
    }
    // Global state
    get randomForceFloorDirection() {
        return wasmFuncs.Level_get_rff_direction(this._ptr);
    }
    set randomForceFloorDirection(val) {
        wasmFuncs.Level_set_rff_direction(this._ptr, val);
    }
    get toggleWallInverted() {
        return !!wasmFuncs.Level_get_toggle_wall_inverted(this._ptr);
    }
    set toggleWallInverted(val) {
        wasmFuncs.Level_set_toggle_wall_inverted(this._ptr, val);
    }
    clone() {
        return new Level(wasmFuncs.Level_clone(this._ptr));
    }
    subticksPassed() {
        return this.currentTick * 3 + this.currentSubtick;
    }
    msecsPassed() {
        return this.subticksPassed() * (1000 / 60);
    }
    hash(settings) {
        return wasmFuncs.Level_hash(this._ptr, settings);
    }
    totalByteSize() {
        return wasmFuncs.Level_total_size(this._ptr);
    }
    get sfx() {
        return Number(wasmFuncs.Level_get_sfx(this._ptr));
    }
    erase(actor) {
        wasmFuncs.Actor_erase(actor._ptr, this._ptr);
    }
}
export var GameState;
(function (GameState) {
    GameState[GameState["PLAYING"] = 0] = "PLAYING";
    GameState[GameState["DEATH"] = 1] = "DEATH";
    GameState[GameState["TIMEOUT"] = 2] = "TIMEOUT";
    GameState[GameState["WON"] = 3] = "WON";
    GameState[GameState["CRASH"] = 4] = "CRASH";
})(GameState || (GameState = {}));
export class CResult extends Struct {
    static alloc() {
        const res = this.allocStruct(8);
        return res;
    }
    static unalloc(ptr) {
        if (!getWasmReader().getInt32(ptr)) {
            wasmFuncs.free(ptr + 4);
        }
    }
    get success() {
        return this.getBool(0);
    }
    get error() {
        if (this.success)
            return null;
        return getStringAt(this.getPtr(4));
    }
    get value() {
        if (!this.success)
            return null;
        return this.getPtr(4);
    }
}
function copyBuffer(buff) {
    const dataPtr = wasmFuncs.malloc(buff.byteLength);
    if (dataPtr === 0)
        throw new Error("Failed to malloc ");
    const memBuf = new Uint8Array(getWasmReader().buffer);
    memBuf.set(new Uint8Array(buff), dataPtr);
    return dataPtr;
}
export function parseC2M(buff) {
    const dataPtr = copyBuffer(buff);
    const levelRes = CResult.alloc();
    wasmFuncs.parse_c2m(levelRes._ptr, dataPtr, buff.byteLength);
    wasmFuncs.free(dataPtr);
    const err = levelRes.error;
    const valPtr = levelRes.value;
    levelRes.free();
    if (err)
        throw new Error(err);
    return new Level(valPtr);
}
export function parseC2MMeta(buff) {
    const dataPtr = copyBuffer(buff);
    const levelRes = CResult.alloc();
    wasmFuncs.parse_c2m_meta(levelRes._ptr, dataPtr, buff.byteLength);
    wasmFuncs.free(dataPtr);
    const err = levelRes.error;
    const valPtr = levelRes.value;
    levelRes.free();
    if (err)
        throw new Error(err);
    return new LevelMetadata(valPtr);
}
//# sourceMappingURL=level.js.map